import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DataGridPro,
  gridClasses,
  GridToolbarProps,
  ToolbarPropsOverrides,
} from "@mui/x-data-grid-pro";
import { JSXElementConstructor, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { columnDef, getGridTexts } from "./columnDef";
import { OtherDealerInventoryGridProps } from "./types";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import { generateDataGridLocaleTexts } from "../../../../../utils/dataGridUtils/utils";
import OtherDealerInventoryToolBar from "../OtherDealerInventoryToolbar/OtherDealerInventoryToolbar";

const OtherDealerInventoryGrid = ({
  data,
  sortModel,
  onSortModelChange,
  toolbarProps,
  isLoading,
  dealerServiceUrl,
}: OtherDealerInventoryGridProps) => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);

  const gridTexts = getGridTexts(t);
  const columns = useMemo(
    () => columnDef(theme, gridTexts, dealerServiceUrl),
    [theme, gridTexts, dealerServiceUrl]
  );

  const getTogglableColumns = (gridColumns: any[]) =>
    gridColumns.map((column) => column.field);
  const dataGridSx = {
    border: 1,
    borderColor: theme.tokens.FuelColorBorderLight,
    borderRadius: theme.tokens.FuelBorderRadiusLg,
    [`.${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
      outline: "none",
    },
    [`& .${gridClasses.cell}`]: {
      display: "flex",
      alignItems: "center",
      border: "none",
      paddingY: "1rem",
      paddingLeft: "1rem",
    },
    [`.MuiDataGrid-withBorderColor`]: {
      borderColor: "transparent",
    },
    [`.MuiIconButton-sizeSmall:hover`]: {
      backgroundColor: theme.tokens.FuelColorBorderDark,
    },
    [`& .${gridClasses.row}`]: {
      borderBottom: 1,
      borderColor: theme.tokens.FuelColorBorderLight,
      alignItems: "center",
      "&:last-child": {
        borderBottom: 0,
      },
    },
    [`.MuiDataGrid-row--dynamicHeight`]: {
      minHeight: "3.5rem !important",
    },
    [`& .${gridClasses.columnHeaders} > div[role="row"]`]: {
      backgroundColor: theme.tokens.FuelColorBackgroundWhite,
      borderRadius: "0.5rem 0.5rem 0 0",
      borderBottom: "1px solid",
      borderColor: theme.tokens.FuelColorBorderLight,
    },
    [`& .${gridClasses.columnHeader}`]: {
      borderTop: 1,
      borderColor: theme.tokens.FuelColorBorderLight,
      padding: "1rem",
    },
    "& .MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
      paddingLeft: 0,
    },
    [`.${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
      {
        outline: "none",
      },
    [`div.MuiDataGrid-topContainer.MuiDataGrid-container--top.css-1oudwrl:: after`]:
      {
        display: "none",
      },
    [`& .${gridClasses.headerFilterRow} > div[role="columnheader"]`]: {
      padding: "0.75rem 1rem",
      borderTop: "none",
    },
    [`& .${gridClasses.toolbarContainer} > button`]: {
      border: "none",
      color: theme.tokens.FuelColorBrandPrimary,
      boxShadow: "none",
    },
    [`.${gridClasses.overlayWrapper}`]: {
      height: "50vh",
    },
    [`.${gridClasses.overlayWrapperInner}`]: {
      height: "100% !important",
    },
    [`.${gridClasses.columnSeparator}`]: {
      color: theme.tokens.FuelColorBorderLight,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: theme.tokens.FuelSpacing4,
      }}
      data-testid="other-dealer-inventory-grid"
    >
      <DataGridPro
        rows={data}
        columns={columns}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        loading={isLoading}
        disableColumnResize
        disableColumnMenu
        disableColumnReorder
        disableColumnFilter
        autoHeight={!(data?.length === 0)}
        getRowHeight={() => "auto"}
        hideFooter
        disableMultipleRowSelection
        sx={dataGridSx}
        slots={{
          loadingOverlay: LoadingIndicator,
          // eslint-disable-next-line react/no-unstable-nested-components
          noRowsOverlay: () => (
            <Box
              sx={{
                display: "flex",
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: theme.tokens.FuelColorTextDisabled }}
              >
                {t("d2d:noDataAvailable.title", "No data available")}
              </Typography>
              <Typography sx={{ color: theme.tokens.FuelColorTextDisabled }}>
                {t(
                  "d2d:noDataAvailable.description",
                  "Please adjust your filters and try again"
                )}
              </Typography>
            </Box>
          ),
          toolbar: OtherDealerInventoryToolBar as JSXElementConstructor<
            GridToolbarProps & ToolbarPropsOverrides
          >,
        }}
        slotProps={{
          toolbar: toolbarProps,
          columnsManagement: { getTogglableColumns },
        }}
        localeText={generateDataGridLocaleTexts(t)}
        disableRowSelectionOnClick
        sortingMode="server"
      />
    </Box>
  );
};

export default OtherDealerInventoryGrid;
